// src/components/laboratory/forms/AdForm.jsx
import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_ADD, UPDATE_ADD } from '../../../api/laboratory-api';
import ModalComponent from '../../common/ModalComponent';
import { Info, Calendar, Package, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const AdForm = ({ 
  laboratory, 
  ad, 
  isOpen, 
  onClose, 
  onSuccess, 
  products = []
}) => {
  const [formData, setFormData] = useState({
    productId: '',
    dateDebut: '',
    dateFin: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data if editing an existing ad
  useEffect(() => {
    if (ad) {
      setFormData({
        productId: ad.productId || '',
        dateDebut: ad.dateDebut ? format(new Date(ad.dateDebut), 'yyyy-MM-dd') : '',
        dateFin: ad.dateFin ? format(new Date(ad.dateFin), 'yyyy-MM-dd') : ''
      });
    } else {
      // Reset form for a new ad
      setFormData({
        productId: '',
        dateDebut: '',
        dateFin: ''
      });
    }
    setFormErrors({});
  }, [ad, isOpen]);

  // Mutations
  const [createAdd] = useMutation(CREATE_ADD, {
    onCompleted: (data) => {
      if (data.createAdd) {
        toast.success('Publicité créée avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la création de la publicité');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la création de la publicité: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const [updateAdd] = useMutation(UPDATE_ADD, {
    onCompleted: (data) => {
      if (data.updateAdd) {
        toast.success('Publicité mise à jour avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la mise à jour de la publicité');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour de la publicité: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.productId) {
      errors.productId = 'Veuillez sélectionner un produit';
    }

    if (!formData.dateDebut) {
      errors.dateDebut = 'La date de début est requise';
    }

    if (!formData.dateFin) {
      errors.dateFin = 'La date de fin est requise';
    }

    if (formData.dateDebut && formData.dateFin) {
      const startDate = new Date(formData.dateDebut);
      const endDate = new Date(formData.dateFin);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        errors.dateDebut = 'La date de début ne peut pas être dans le passé';
      }

      if (endDate <= startDate) {
        errors.dateFin = 'La date de fin doit être après la date de début';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission handler
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const adInput = {
        productId: formData.productId,
        dateDebut: new Date(formData.dateDebut).toISOString(),
        dateFin: new Date(formData.dateFin).toISOString(),
        laboratoryId: laboratory.id
      };

      if (ad) {
        // Update existing ad
        adInput.id = ad.id;
        await updateAdd({
          variables: {
            updateAddInput: adInput
          }
        });
      } else {
        // Create new ad
        await createAdd({
          variables: {
            createAddInput: adInput
          }
        });
      }
      
      // Success is handled in the mutation callbacks
    } catch (error) {
      console.error('Error saving ad:', error);
      setIsSubmitting(false);
    }
  };

  // Get selected product details
  const selectedProduct = products.find(p => p.id === formData.productId);

  // Calculate ad duration
  const getAdDuration = () => {
    if (formData.dateDebut && formData.dateFin) {
      const startDate = new Date(formData.dateDebut);
      const endDate = new Date(formData.dateFin);
      const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      return duration > 0 ? duration : 0;
    }
    return 0;
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={ad ? 'Modifier la publicité' : 'Créer une nouvelle publicité'}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      size="lg"
    >
      <div className="space-y-6">
        {/* Laboratory info */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-blue-600 mr-2" />
            <p className="text-blue-800 font-medium">
              Laboratoire: {laboratory?.nom}
            </p>
          </div>
        </div>

        {/* Product selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Produit à promouvoir <span className="text-red-500">*</span>
          </label>
          <select
            name="productId"
            value={formData.productId}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              formErrors.productId ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Sélectionner un produit</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>
                {product.name} - {product.price?.toFixed(2)} DH
              </option>
            ))}
          </select>
          {formErrors.productId && (
            <p className="mt-1 text-sm text-red-600">{formErrors.productId}</p>
          )}
        </div>

        {/* Selected product preview */}
        {selectedProduct && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Produit sélectionné</h4>
            <div className="flex items-center space-x-4">
              {selectedProduct.image ? (
                <img
                  className="h-16 w-16 rounded-md object-cover"
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                />
              ) : (
                <div className="h-16 w-16 rounded-md bg-gray-200 flex items-center justify-center">
                  <Package className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedProduct.description?.substring(0, 100)}
                  {selectedProduct.description?.length > 100 ? '...' : ''}
                </p>
                <p className="text-sm font-semibold text-green-600 mt-1">
                  {selectedProduct.price?.toFixed(2)} DH
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Date inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                name="dateDebut"
                value={formData.dateDebut}
                onChange={handleInputChange}
                min={format(new Date(), 'yyyy-MM-dd')}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.dateDebut ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.dateDebut && (
              <p className="mt-1 text-sm text-red-600">{formErrors.dateDebut}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                name="dateFin"
                value={formData.dateFin}
                onChange={handleInputChange}
                min={formData.dateDebut || format(new Date(), 'yyyy-MM-dd')}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.dateFin ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.dateFin && (
              <p className="mt-1 text-sm text-red-600">{formErrors.dateFin}</p>
            )}
          </div>
        </div>

        {/* Duration info */}
        {formData.dateDebut && formData.dateFin && getAdDuration() > 0 && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center">
              <Info className="h-5 w-5 text-green-600 mr-2" />
              <div>
                <p className="text-green-800 font-medium">
                  Durée de la publicité: {getAdDuration()} jour{getAdDuration() > 1 ? 's' : ''}
                </p>
                <p className="text-green-700 text-sm">
                  Du {format(new Date(formData.dateDebut), 'dd MMMM yyyy', { locale: fr })} au{' '}
                  {format(new Date(formData.dateFin), 'dd MMMM yyyy', { locale: fr })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Warning for overlapping ads */}
        {selectedProduct && formData.dateDebut && formData.dateFin && (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
              <div>
                <p className="text-yellow-800 font-medium text-sm">
                  Information importante
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  Assurez-vous qu'aucune autre publicité pour ce produit n'est déjà programmée 
                  sur cette période pour éviter les conflits.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Additional info */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Informations sur la publicité
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• La publicité sera visible dans la section "Dernières publicités" de l'application</li>
            <li>• Les utilisateurs pourront voir les détails du produit promu</li>
            <li>• La publicité s'activera automatiquement à la date de début</li>
            <li>• Vous pouvez modifier ou supprimer la publicité avant qu'elle ne commence</li>
          </ul>
        </div>

        {/* Form validation summary */}
        {Object.keys(formErrors).length > 0 && (
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium text-sm">
                  Veuillez corriger les erreurs suivantes:
                </p>
                <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                  {Object.values(formErrors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
};

export default AdForm;